import ReactApexChart from "react-apexcharts";
import styles from "./TimeLineGroup.module.scss";
import GroupDate from "../GroupDate";
import HeaderItem from "../HeaderItem";
import {
  getZonedTime,
  toZonedUtcMs,
  getZonedDayBoundsMs,
  SYSTEM_TIMEZONE,
} from "utils/trackingTransform";
import { createEmailsField } from "../../utils";

const TimeLineGroup = ({ group, photos, timezone = SYSTEM_TIMEZONE }) => {
  const dayKey = Object.keys(group)[0];
  const date = new Date(dayKey);
  const info = Object.values(group)[0];
  const dayBounds = getZonedDayBoundsMs(dayKey);

  const tzMs = (ts) => toZonedUtcMs(ts, timezone);
  const tzTime = (ts) => getZonedTime(ts, timezone);

  function infoPopup(name, photo, data, index) {
    const currentData = data[index];

    return `<div class=${styles.popup}>
      <div class=${styles.user}>
        <img
          class=${styles.photo}
          src=${photo}
          alt=${name}
        />
        ${name}
      </div>
      <div class=${styles.popup_title}>
        ${
          !currentData?.is_incoming && !currentData?.sent_date && currentData?.call_date_time
            ? "Chat Bid"
            : currentData?.is_incoming === "inbound"
            ? "Incoming Call"
            : currentData?.is_incoming === "outbound"
            ? "Outgoing Call"
            : "Outgoing Email"
        }
      </div>
      <div class=${styles.popup_table}>
        ${
          currentData?.call_date_time
            ? `<span class=${styles.popup_label}>Start:</span>
          <span class=${styles.popup_value}>
            ${tzTime(currentData?.call_date_time)}
          </span>`
            : ""
        }
        ${
          currentData?.call_date_time_end
            ? `<span class=${styles.popup_label}>End:</span>
          <span class=${styles.popup_value}>
            ${tzTime(currentData?.call_date_time_end)}
          </span>`
            : ""
        }
        ${
          currentData?.duration
            ? `<span class=${styles.popup_label}>Duration:</span>
              <span class=${styles.popup_value}>${currentData?.duration}</span>`
            : ""
        }

        ${
          currentData?.sent_date
            ? `<span class=${styles.popup_label}>Time Sent</span>
              <span class=${styles.popup_value}>${tzTime(currentData?.sent_date)}</span>`
            : ""
        }
        ${
          currentData?.email_from
            ? `<span class=${styles.popup_label}>From</span><span class=${
                styles.popup_value
              }>${createEmailsField(currentData?.email_from)}</span>`
            : ""
        }
        ${
          currentData?.email_to
            ? `<span class=${styles.popup_label}>To</span><span class=${
                styles.popup_value
              }>${createEmailsField(currentData?.email_to)}</span>`
            : ""
        }
        ${
          currentData?.email_cc !== undefined
            ? `<span class=${styles.popup_label}>CC</span><span class=${
                styles.popup_value
              }>${
                currentData?.email_cc ? createEmailsField(currentData?.email_cc) : "—"
              }</span>`
            : ""
        }
        ${
          currentData?.email_bcc !== undefined
            ? `<span class=${styles.popup_label}>BCC</span><span class=${
                styles.popup_value
              }>${
                currentData?.email_bcc ? createEmailsField(currentData?.email_bcc) : "—"
              }</span>`
            : ""
        }
        ${
          currentData?.subject
            ? `<span class=${styles.popup_label}>Subject</span><span class=${styles.popup_value}>${currentData?.subject}</span>`
            : ""
        }
      </div>
      ${
        currentData?.destination_or_caller
          ? `<div class=${styles.info}>
              <span class=${styles.popup_label}>${
              currentData?.is_incoming === "inbound" ? "Caller:" : "Destination:"
            }</span>
              <span class=${styles.popup_value}>${
              currentData?.destination_or_caller
            }</span>
            <div>`
          : ""
      }
    </div>`;
  }

  function setOptions(name, photo, inboundGroup, outboundGroup, emails, chatBids) {
    return {
      colors: ["#D9D9D9", "#167CB8", "#C74246"],
      stroke: {
        width: [0, 0, 0, 1],
        curve: "straight",
        colors: ["#D9D9D9"],
        dashArray: [0, 0, 0, 1],
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
      chart: {
        fontFamily: "Poppins SemiBold",
        height: 180,
        type: "rangeBar",

        foreColor: "#3F3F40",
        toolbar: {
          tools: {
            download: false,
            reset: false,
            pan: false,
          },
        },
        zoom: {
          type: "x",
          enabled: true,
          autoScaleYaxis: true,
        },

        events: {
          zoomed: function (chartContext, { xaxis }) {
            const min = xaxis.min;
            const max = xaxis.max;
            const tenMinutes = 10 * 60 * 1000;
            if (max - min < tenMinutes) {
              chartContext.updateOptions({
                xaxis: {
                  min: max - tenMinutes,
                  max: max,
                },
              });
            }
          },

          mounted: (chartContext) => {
            return chartContext?.toolbar?.elZoomOut?.style?.setProperty(
              "pointer-events",
              "none",
            );
          },
          beforeZoom: function (chartContext, { xaxis }) {
            if (xaxis?.min <= dayBounds.start && xaxis?.max >= dayBounds.end) {
              return {
                xaxis: { min: dayBounds.start, max: dayBounds.end },
              };
            }
          },
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        intersect: true,
        enabledOnSeries: [1, 2, 3, 4],
        custom: function ({ seriesIndex, dataPointIndex }) {
          if (seriesIndex === 1) {
            return infoPopup(name, photo, inboundGroup, dataPointIndex);
          } else if (seriesIndex === 2) {
            return infoPopup(name, photo, outboundGroup, dataPointIndex);
          } else if (seriesIndex === 3) {
            return infoPopup(name, photo, emails, dataPointIndex);
          } else if (seriesIndex === 4) {
            return infoPopup(name, photo, chatBids, dataPointIndex);
          }
        },
      },
      grid: {
        show: false,
        borderColor: "#fff",
        xaxis: {
          lines: {
            show: false,
          },
        },
        row: {
          colors: ["#fff"],
          opacity: 1,
        },
        padding: {
          left: 5,
          right: 20,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "100%",
          rangeBarGroupRows: true,
        },
      },
      xaxis: {
        type: "datetime",
        min: dayBounds.start,
        max: dayBounds.end,
        axisTicks: {
          show: false,
        },
        labels: {
          hideOverlappingLabels: false,
          style: {
            fontSize: "12px",
          },
          offsetY: 5,
          formatter: function (value) {
            // toZonedUtcMs output already has wall time in selected TZ encoded as UTC
            const date = new Date(value);
            const hours = date.getUTCHours().toString().padStart(2, "0");
            const minutes = date.getUTCMinutes().toString().padStart(2, "0");
            return `${hours}:${minutes}`;
          },
        },
      },
      yaxis: {
        show: false,
      },
    };
  }

  return (
    <div className={styles.wrapper}>
      <GroupDate date={date} />
      {info &&
        Object.values(info)?.map((data, index) => {
          const inboundGroup = data?.calls?.filter(
            (item) => item?.is_incoming === "inbound",
          ) ?? [];
          const outboundGroup = data?.calls?.filter(
            (item) => item?.is_incoming === "outbound",
          ) ?? [];
          const chatBids = data?.chat_bids ?? [];

          const series = [
            {
              name: "timetracking",
              data: (data?.timetracking ?? []).map(({ clock_in, clock_out }) => ({
                x: "line",
                y: [tzMs(clock_in), tzMs(clock_out)],
              })),
            },
            {
              name: "inbound",
              data: inboundGroup.map(({ call_date_time, call_date_time_end }) => ({
                x: "line",
                y: [tzMs(call_date_time), tzMs(call_date_time_end)],
              })),
            },
            {
              name: "outbound",
              data: outboundGroup.map(({ call_date_time, call_date_time_end }) => ({
                x: "line",
                y: [tzMs(call_date_time), tzMs(call_date_time_end)],
              })),
            },
            {
              name: "emails",
              data: (data?.emails ?? []).map(({ sent_date }) => {
                const start = tzMs(sent_date);
                return {
                  x: "line",
                  y: [start, start + 60 * 1000],
                  fillColor: "#00C193",
                };
              }),
            },
            {
              name: "chat_bids",
              data: chatBids.map(({ call_date_time }) => {
                const start = tzMs(call_date_time);
                return {
                  x: "line",
                  y: [start, start + 60 * 1000],
                  fillColor: "#F5A623",
                };
              }),
            },
            {
              name: "scheduled",
              data: [
                {
                  x: "line",
                  goals: [
                    {
                      name: "Break",
                      value: data?.scheduled_shift_start ? tzMs(data.scheduled_shift_start) : null,
                      strokeColor: "#3F3F40",
                      strokeWidth: 6,
                      strokeHeight: 60,
                      strokeLineCap: "round",
                    },
                    {
                      name: "Break",
                      value: data?.scheduled_shift_end ? tzMs(data.scheduled_shift_end) : null,
                      strokeColor: "#3F3F40",
                      strokeWidth: 6,
                      strokeHeight: 60,
                      strokeLineCap: "round",
                    },
                  ].filter((g) => g.value != null),
                },
              ],
            },
          ];
          return (
            <div className={styles.item} key={index}>
              <HeaderItem photos={photos} info={info} index={index} data={data} />
              <div className={styles.body}>
                <ReactApexChart
                  options={setOptions(
                    `${data?.first_name} ${data?.last_name}`,
                    photos[Object.keys(info)[index]],
                    inboundGroup,
                    outboundGroup,
                    data?.emails ?? [],
                    chatBids,
                  )}
                  series={series}
                  type="rangeBar"
                  height={180}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default TimeLineGroup;
